// src/api/clientCard.ts
import axiosClient from './axiosClient';

/** Телефон клиента в трёх видах: как пришёл, нормализованный, для показа. */
export interface ClientCardPhone {
    input: string;
    comparable: string;
    display: string;
}

export interface ClientCardContact {
    id: string | null;
    name: string | null;
    company: string | null;
    tags: string[];
    source: string | null;
    isOnline: boolean;
    lastSeen: string | null;
}

export interface ClientCardChats {
    conversationCount: number;
    messageCount: number;
    lastMessageAt: string | null;
    channels: string[];
}

export interface ClientCardCall {
    id: string;
    direction: string;
    directionLabel?: string;
    status: string;
    statusLabel?: string;
    resultLabel: string | null;
    startedAt: string | null;
    durationSeconds: number | null;
    managerName: string | null;
}

export interface ClientCardCalls {
    total: number;
    missed: number;
    lastCallAt: string | null;
    recent: ClientCardCall[];
}

export interface ClientCardLesson {
    id: number | null;
    title: string | null;
    startAt: string | null;
    status: string;
    employee: string | null;
}

export interface ClientCardLessons {
    bookedRecently: boolean;
    lastLessonAt: string | null;
    nextLessonAt: string | null;
    appointments: ClientCardLesson[];
}

/**
 * Клиент из справочника организации, если бэкенд нашёл его по номеру.
 *
 * Блок необязательный: в карточке по одной беседе клиента может не быть
 * в справочнике, тогда `client` = null (или ключа нет вовсе).
 */
export interface ClientCardClient {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    company: string | null;
    note: string | null;
    channel: string | null;
}

export interface ClientCardOrganization {
    id: string | null;
    name: string;
}

export interface ClientCardManager {
    name: string;
    number: string | null;
}

export interface ClientCard {
    phone: ClientCardPhone;
    contact: ClientCardContact | null;
    /** Клиент из справочника организации; null — клиента ещё нет. */
    client?: ClientCardClient | null;
    chats: ClientCardChats;
    calls: ClientCardCalls;
    lessons: ClientCardLessons;
    organization: ClientCardOrganization | null;
    manager: ClientCardManager | null;
}

export interface ClientCardParams {
    /** Организация консоли: без неё бэкенд не отдаёт карточку. */
    mode: string;
    phone?: string | null;
    conversationId?: string | null;
}

/**
 * Карточка клиента для окна диалога.
 *
 * baseURL уже содержит /api, поэтому путь начинается с /v1.
 * Номер можно не передавать, если известен id беседы: бэкенд достанет
 * телефон из беседы (в сериализации чата номера нет).
 */
export const fetchClientCard = async ({ mode, phone, conversationId }: ClientCardParams): Promise<ClientCard> => {
    const response = await axiosClient.get<ClientCard>('/v1/clients/card', {
        params: {
            mode,
            ...(phone ? { phone } : {}),
            ...(conversationId ? { conversationId } : {}),
        },
    });

    return response.data;
};
